import { SatelliteTouringStatus } from '@zg-rentals/ts-satellite-client';
import { TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import { useTourForSatelliteConversationId } from '@zg-rentals/touring';
import { useContext } from 'react';
import { Box, Paragraph, Tag } from '@zillow/constellation-10';
import TourStatusCard from '../../TourStatusCard';
import TourInfoUnavailableCard from '../../TourStatusCard/TourInfoUnavailableCard';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import { useCachedLeadByConversationId } from '../../../hooks/useLeadManagementTableQuery';

type RenterDetailsTourProps = {
  leadId?: string | null;
};

export default function RenterDetailsTour({ leadId }: RenterDetailsTourProps) {
  const { conversationId } = useContext(LeadManagementContext);
  const lead = useCachedLeadByConversationId(conversationId);

  // The tour also comes from z-calendar, so don't fetch it once satellite has already failed to reach z-calendar.
  if (lead?.touring.touringStatus === SatelliteTouringStatus.UNAVAILABLE) {
    return (
      <>
        <Box css={{ marginTop: 'loose', marginBottom: 'default' }}>
          <Paragraph textStyle="body-sm-bold">Tour</Paragraph>
        </Box>
        <TourInfoUnavailableCard />
      </>
    );
  }

  return (
    <RenterDetailsTourCard
      conversationId={conversationId}
      leadId={leadId}
      listingAlias={lead?.listingDetails?.listingAlias ?? ''}
      renterName={lead?.renterInfo?.renterName ?? ''}
    />
  );
}

type RenterDetailsTourCardProps = RenterDetailsTourProps & {
  conversationId: string;
  listingAlias: string;
  renterName: string;
};

function RenterDetailsTourCard({ conversationId, leadId, listingAlias, renterName }: RenterDetailsTourCardProps) {
  const { data: tourData, isLoading } = useTourForSatelliteConversationId(
    {
      conversationId,
      listingAlias,
    },
    {
      errorDetails: 'RenterDetailsTour - Error fetching tour for conversationId',
    },
  );

  if (!isLoading && !tourData?.tour) return null;

  const isTourRequested = tourData?.tour?.tourStatus === TourStatusForZcheduler.REQUESTED;

  return (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 'tighter',
          marginTop: 'loose',
          marginBottom: 'default',
        }}
      >
        <Paragraph textStyle="body-sm-bold">Tour</Paragraph>
        {isTourRequested && (
          <Tag size="sm" tone="warning">
            Action needed
          </Tag>
        )}
      </Box>
      <TourStatusCard
        conversationId={conversationId}
        leadId={leadId}
        listingAlias={listingAlias}
        renterName={renterName}
        showViewInCalendarLink={true}
      />
    </>
  );
}
